<script>
/**
 * @component CalendarView
 * @description Vista de agenda estilo Google Calendar con grilla horaria semanal.
 *              Muestra slots como bloques posicionados con colores por estado.
 *              Soporta vistas día/semana/mes. Doctor auto-carga su agenda;
 *              Admin selecciona doctor y opcionalmente filtra por especialidad.
 * @example <CalendarView />
 */
import {
  format,
  addDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfDay,
  isSameDay,
  isBefore,
  getDay,
} from "date-fns";
import { es } from "date-fns/locale";
import { mapGetters } from "vuex";
import {
  startOfDayAR,
  endOfDayAR,
  toApiUTC,
  formatTimeAR,
} from "@/services/datetime";
import Spinner from "@/components/ui/Spinner";
import ModalAppointment from "@/components/appointment/ModalAppointment";
import AdmissionDialog from "@/components/appointment/AdmissionDialog.vue";
import BaseSubNav from "@/components/ui/BaseSubNav.vue";
import slotService from "@/services/slot";
import doctorService from "@/services/doctor";
import userService from "@/services/user";
import appointmentService from "@/services/appointment";
import helperService from "@/services/helper";
import { isAppointmentBookingEnabled } from "@/services/config";

/** @description Hora de inicio de la grilla */
const GRID_START_HOUR = 7;
/** @description Hora de fin de la grilla */
const GRID_END_HOUR = 21;
/** @description Pixeles por cada bloque de 30 minutos */
const PX_PER_30MIN = 48;

export default {
  name: "CalendarView",

  components: {
    Spinner,
    ModalAppointment,
    AdmissionDialog,
    BaseSubNav,
  },

  data() {
    return {
      currentDate: new Date(),
      viewMode: "week",
      doctors: [],
      doctorDetail: null,
      selectedDoctorId: "",
      selectedSpecialtyFilter: "",
      slots: [],
      isLoading: false,
      isLoadingDoctors: false,
      isModalVisible: false,
      selectedSlot: {},
      isAppointmentBookingEnabled,
      admissionDialogVisible: false,
      GRID_START_HOUR,
      PX_PER_30MIN,
    };
  },

  computed: {
    ...mapGetters([
      "isAdmin",
      "isDoctor",
      "isPatient",
      "isHealthAdmin",
      "user",
    ]),

    /**
     * @description Si el usuario es admin o health-admin
     * @returns {boolean}
     */
    isAdminLike() {
      return this.isAdmin || this.isHealthAdmin;
    },

    /**
     * @description Horas visibles en la grilla (7:00 a 20:30)
     * @returns {Array<{hour: number, label: string}>}
     */
    gridHours() {
      const hours = [];
      for (let h = GRID_START_HOUR; h < GRID_END_HOUR; h++) {
        hours.push({
          hour: h,
          label: `${String(h).padStart(2, "0")}:00`,
        });
      }
      return hours;
    },

    /**
     * @description Altura total de la grilla en px
     * @returns {number}
     */
    gridHeight() {
      return (GRID_END_HOUR - GRID_START_HOUR) * PX_PER_30MIN * 2;
    },

    /**
     * @description Días a mostrar según el modo de vista
     * @returns {Array<Date>}
     */
    visibleDays() {
      if (this.viewMode === "day") {
        return [this.currentDate];
      }
      if (this.viewMode === "week") {
        const start = startOfWeek(this.currentDate, { weekStartsOn: 1 });
        const days = [];
        for (let i = 0; i < 7; i++) {
          days.push(addDays(start, i));
        }
        return days;
      }
      // month
      const start = startOfMonth(this.currentDate);
      const end = endOfMonth(this.currentDate);
      const days = [];
      let current = start;
      while (current <= end) {
        days.push(current);
        current = addDays(current, 1);
      }
      return days;
    },

    /**
     * @description Título de navegación (ej: "Febrero 2026", "Lun 24 Feb 2026")
     * @returns {string}
     */
    navigationTitle() {
      if (this.viewMode === "day") {
        return format(this.currentDate, "EEEE d 'de' MMMM yyyy", {
          locale: es,
        });
      }
      if (this.viewMode === "week") {
        const start = startOfWeek(this.currentDate, { weekStartsOn: 1 });
        const end = endOfWeek(this.currentDate, { weekStartsOn: 1 });
        const startStr = format(start, "d MMM", { locale: es });
        const endStr = format(end, "d MMM yyyy", { locale: es });
        return `${startStr} – ${endStr}`;
      }
      return format(this.currentDate, "MMMM yyyy", { locale: es });
    },

    /**
     * @description Indica si se puede cargar slots (solo requiere doctor seleccionado)
     * @returns {boolean}
     */
    canLoadSlots() {
      return !!this.selectedDoctorId;
    },

    /**
     * @description Especialidades médicas del doctor cargado
     * @returns {Array}
     */
    medicalSpecialties() {
      return this.doctorDetail?.doctor?.medicalSpecialties || [];
    },

    /**
     * @description Si mostrar el selector de especialidad
     * @returns {boolean}
     */
    showSpecialtyFilter() {
      if (!this.canLoadSlots) return false;
      if (this.isAdminLike) return this.medicalSpecialties.length > 0;
      return this.medicalSpecialties.length > 1;
    },

    /**
     * @description Especialidad médica para el modal (auto-selecciona si hay una sola)
     * @returns {Object|undefined}
     */
    getSelectedMedicalSpecialty() {
      if (this.selectedSpecialtyFilter) {
        return this.medicalSpecialties?.find(
          (ms) => ms._id === this.selectedSpecialtyFilter
        );
      }
      if (this.medicalSpecialties.length === 1) {
        return this.medicalSpecialties[0];
      }
      return undefined;
    },

    /**
     * @description Slots filtrados opcionalmente por especialidad (client-side)
     * @returns {Array}
     */
    filteredSlots() {
      if (!this.selectedSpecialtyFilter) return this.slots;
      return this.slots.filter((slot) =>
        slot.medicalSpecialties?.some(
          (ms) => (ms._id || ms) === this.selectedSpecialtyFilter
        )
      );
    },
  },

  watch: {
    selectedDoctorId() {
      this.slots = [];
      this.selectedSpecialtyFilter = "";
      if (this.selectedDoctorId) {
        this.loadDoctorAndSlots(this.selectedDoctorId);
      }
    },

    selectedSpecialtyFilter() {
      // filteredSlots computed se recalcula automáticamente
    },

    currentDate() {
      this.fetchSlots();
    },

    viewMode() {
      this.fetchSlots();
    },
  },

  created() {
    this.$bus.$on("appointment-created", (slot, appointment) => {
      const index = this.slots.findIndex((s) => s._id === slot._id);
      if (index >= 0) {
        // Slot existente: actualizar con la cita asignada
        this.$set(this.slots, index, {
          ...this.slots[index],
          available: false,
          appointment,
        });
      } else if (!slot._id) {
        // Sobreturno: el slot no existía, agregarlo al calendario con flag isInstant
        this.slots.push({
          ...slot,
          _id: appointment.slot?._id || appointment._id,
          available: false,
          appointment,
          isInstant: true,
        });
      }
    });

    this.$bus.$on("appointment-cancelled", (cancelledAppointment) => {
      const index = this.slots.findIndex(
        (s) => s.appointment?._id === cancelledAppointment._id
      );
      if (index >= 0) {
        this.$set(this.slots, index, {
          ...this.slots[index],
          available: true,
          appointment: undefined,
        });
      }
    });

    if (this.isDoctor) {
      this.initDoctor();
    } else {
      this.loadAllDoctors();
    }
  },

  methods: {
    /**
     * @description Navega al día de hoy
     */
    goToToday() {
      this.currentDate = new Date();
    },

    /**
     * @description Navega al período anterior
     */
    goPrev() {
      if (this.viewMode === "day") {
        this.currentDate = addDays(this.currentDate, -1);
      } else if (this.viewMode === "week") {
        this.currentDate = addDays(this.currentDate, -7);
      } else {
        const d = new Date(this.currentDate);
        d.setMonth(d.getMonth() - 1);
        this.currentDate = d;
      }
    },

    /**
     * @description Navega al período siguiente
     */
    goNext() {
      if (this.viewMode === "day") {
        this.currentDate = addDays(this.currentDate, 1);
      } else if (this.viewMode === "week") {
        this.currentDate = addDays(this.currentDate, 7);
      } else {
        const d = new Date(this.currentDate);
        d.setMonth(d.getMonth() + 1);
        this.currentDate = d;
      }
    },

    /**
     * @description Inicializa vista para doctor logueado: carga su detalle y slots
     */
    async initDoctor() {
      const doctorId = this.$store.state.user._id;
      try {
        const doctorDetails = await doctorService.getDoctorDetails(doctorId);
        this.doctorDetail = doctorDetails;
        this.doctors = [doctorDetails];
        this.selectedDoctorId = doctorDetails._id;
      } catch (err) {
        console.error("Error cargando detalle del doctor:", err);
      }
    },

    /**
     * @description Carga todos los doctores para selector de admin
     */
    async loadAllDoctors() {
      this.isLoadingDoctors = true;
      try {
        const res = await userService.getDoctors({ limit: 500 });
        this.doctors = res.docs || res;
      } catch (err) {
        console.error("Error cargando doctores:", err);
      } finally {
        this.isLoadingDoctors = false;
      }
    },

    /**
     * @description Carga detalle del doctor seleccionado y luego sus slots
     * @param {string} doctorId - ID del doctor
     */
    async loadDoctorAndSlots(doctorId) {
      try {
        const doctorDetails = await doctorService.getDoctorDetails(doctorId);
        this.doctorDetail = doctorDetails;
      } catch (err) {
        console.error("Error cargando detalle del doctor:", err);
      }
      this.fetchSlots();
    },

    /**
     * @description UC-REG-079. Inicio del dia en TZ del tenant, devuelto
     *              como ISO UTC para mandar al API. Reemplaza el workaround
     *              historico que sumaba/restaba el offset del browser a
     *              mano — eso solo funcionaba si el dev/PM abria desde un
     *              browser configurado en el mismo huso del tenant.
     * @param {Date|string} date
     * @returns {string} ISO 8601 UTC
     */
    getDateFirstHourOfDay(date) {
      return toApiUTC(startOfDayAR(date));
    },

    /**
     * @description UC-REG-079. Fin del dia en TZ del tenant como ISO UTC.
     * @param {Date|string} date
     * @returns {string}
     */
    getDateLastHourOfDay(date) {
      return toApiUTC(endOfDayAR(date));
    },

    /**
     * @description Carga slots para el rango visible.
     *              - Doctor: usa /slots/doctor/:id (acceso propio)
     *              - Admin/HealthAdmin: usa /slots con filtro por doctor (acceso amplio)
     */
    async fetchSlots() {
      if (!this.canLoadSlots) return;

      this.isLoading = true;
      this.slots = [];

      try {
        const days = this.visibleDays;
        const firstDay = days[0];
        const lastDay = days[days.length - 1];

        // Populate mínimo para minimizar round-trips a Mongo: cada path de
        // populate es una query separada y, con Render y Atlas en regiones
        // distintas, cada una cuesta ~70ms de red. La grilla solo necesita
        // el paciente de la cita (nombre + color) y el doctor (lo muestra el
        // modal de detalle). videoRoom, medicalSpecialty e institution se
        // re-piden on-demand desde el modal; el filtro de especialidad
        // compara IDs crudos de slot.medicalSpecialties, sin populate.
        const populate = [
          {
            path: "appointment",
            populate: [{ path: "patient" }],
          },
          { path: "doctor" },
        ];

        let res;
        if (this.isDoctor) {
          // Doctor: endpoint propio con acceso completo a su agenda
          res = await slotService.getSlotsByDoctor({
            doctorId: this.selectedDoctorId,
            params: {
              startsAt: { $gte: this.getDateFirstHourOfDay(firstDay) },
              endsAt: { $lte: this.getDateLastHourOfDay(lastDay) },
              populate,
              limit: 500,
            },
          });
        } else {
          // Admin / HealthAdmin: endpoint genérico GET /slots con filtro por doctor
          res = await slotService.getSlotsByDate({
            doctor: this.selectedDoctorId,
            startsAt: { $gte: this.getDateFirstHourOfDay(firstDay) },
            endsAt: { $lte: this.getDateLastHourOfDay(lastDay) },
            populate,
            limit: 500,
          });
        }

        this.slots = res.docs || res;
      } catch (err) {
        console.error("Error cargando slots:", err);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * @description Filtra slots para un día concreto (usa filteredSlots)
     * @param {Date} day - Fecha del día
     * @returns {Array}
     */
    slotsForDay(day) {
      return this.filteredSlots.filter((slot) => {
        const slotDate = new Date(slot.startsAt);
        return isSameDay(slotDate, day);
      });
    },

    /**
     * @description Calcula posición top de un slot en la grilla
     * @param {Object} slot - Slot
     * @returns {number} px desde el top de la grilla
     */
    slotTop(slot) {
      const d = new Date(slot.startsAt);
      const h = d.getHours();
      const m = d.getMinutes();
      const totalMinutes = (h - GRID_START_HOUR) * 60 + m;
      return (totalMinutes / 30) * PX_PER_30MIN;
    },

    /**
     * @description Calcula altura de un slot en la grilla
     * @param {Object} slot - Slot
     * @returns {number} Altura en px
     */
    slotHeight(slot) {
      const start = new Date(slot.startsAt);
      const end = new Date(slot.endsAt);
      const durationMinutes = (end - start) / (1000 * 60);
      const height = (durationMinutes / 30) * PX_PER_30MIN;
      return Math.max(height, 20);
    },

    /**
     * @description Clase CSS para el bloque del slot
     * @param {Object} slot - Slot
     * @returns {string}
     */
    slotClass(slot) {
      if (!slot.available && !slot.appointment) return "cal-slot--disabled";
      if (!slot.available && slot.appointment) {
        // Sobreturno: contempla tanto el flag client-side recién creado
        // (slot.isInstant) como el persistido en la cita
        // (appointment.isInstant), para que el color naranja sobreviva
        // al refresh de la página — al recargar, el slot viene del
        // backend sin slot.isInstant pero la cita conserva isInstant.
        const isInstant =
          slot.isInstant || (slot.appointment && slot.appointment.isInstant);
        return isInstant ? "cal-slot--instant" : "cal-slot--busy";
      }
      return "cal-slot--free";
    },

    /**
     * @description Label corto del slot
     * @param {Object} slot - Slot
     * @returns {string}
     */
    slotLabel(slot) {
      if (slot.available) return "Disponible";
      if (slot.appointment) {
        if (this.isPatient && slot.appointment.patient?._id !== this.user._id) {
          return "Ocupado";
        }
        return this.getFullName(slot.appointment.patient);
      }
      return "No disponible";
    },

    /**
     * @description UC-REG-080: Devuelve los iconos que representan los
     *              tipos de atención del slot/appointment para mostrarlos
     *              dentro del bloque del calendario.
     *
     *              Lógica de prioridad:
     *              1. Si el slot tiene appointment → usar el attentionType
     *                 del appointment (string, no array) — es el tipo final
     *                 acordado con el paciente.
     *              2. Si el slot está libre → usar slot.attentionType (array)
     *                 — qué admite el slot todavía.
     *              3. Default: nada (slot deshabilitado u otros).
     *
     *              Material Icons usados:
     *              - `local_hospital` → presencial (in-person)
     *              - `videocam` → virtual
     * @param {Object} slot
     * @returns {Array<{icon:string, label:string}>}
     */
    attentionTypeIcons(slot) {
      const types = new Set();
      if (slot.appointment && slot.appointment.attentionType) {
        // appointment.attentionType es string ("virtual"|"in-person")
        types.add(slot.appointment.attentionType);
      } else if (Array.isArray(slot.attentionType)) {
        slot.attentionType.forEach((t) => types.add(t));
      }
      const result = [];
      if (types.has("in-person")) {
        result.push({ icon: "local_hospital", label: "Presencial" });
      }
      if (types.has("virtual")) {
        result.push({ icon: "videocam", label: "Virtual" });
      }
      return result;
    },

    /**
     * @description Tooltip combinado con todos los tipos de atención
     *              admitidos/agendados, separados por " · ". Vacío si no hay.
     * @param {Object} slot
     * @returns {string}
     */
    attentionTypeTooltip(slot) {
      const icons = this.attentionTypeIcons(slot);
      if (!icons.length) return "";
      return icons.map((i) => i.label).join(" · ");
    },

    /**
     * @description Horario formateado de un slot (solo hora inicio para compactar)
     * @param {Object} slot - Slot
     * @returns {string}
     */
    /**
     * @description UC-REG-079: TZ del tenant.
     */
    slotTime(slot) {
      return formatTimeAR(slot.startsAt);
    },

    /**
     * @description Formatea nombre de día para el header de la grilla
     * @param {Date} day - Fecha
     * @returns {string}
     */
    formatDayHeader(day) {
      return format(day, "EEE", { locale: es }).toUpperCase();
    },

    /**
     * @description Formatea número de día
     * @param {Date} day - Fecha
     * @returns {string}
     */
    formatDayNumber(day) {
      return format(day, "d");
    },

    /**
     * @description Verifica si un día es hoy
     * @param {Date} day - Fecha
     * @returns {boolean}
     */
    isToday(day) {
      return isSameDay(day, new Date());
    },

    /**
     * @description Verifica si un día ya pasó (anterior a hoy a las 00:00).
     *              Se usa para griseaar las columnas/celdas pasadas — el
     *              calendario es prospectivo; los atendidos van en "Atenciones".
     * @param {Date} day - Fecha
     * @returns {boolean}
     */
    isPastDay(day) {
      return isBefore(day, startOfDay(new Date()));
    },

    /**
     * @description Verifica si un slot pertenece a un día ya pasado.
     * @param {Object} slot - Slot con `endsAt`
     * @returns {boolean}
     */
    isPastSlot(slot) {
      return isBefore(new Date(slot.endsAt), startOfDay(new Date()));
    },

    /**
     * @description Devuelve la columna del grid (1-7, Lunes=1, Domingo=7) donde
     *   debe ubicarse el primer día del mes. Sin este offset, el día 1 siempre
     *   caía en la columna 1 (Lunes) y todo el mes quedaba corrido —
     *   bug BUG-003 del reporte de QA navegador.
     * @param {Date} day - Primer día del mes
     * @returns {number} Columna 1-7
     */
    monthStartColumn(day) {
      // getDay: 0=Domingo, 1=Lunes, ..., 6=Sábado. La grilla es Lunes-Domingo.
      const d = getDay(day);
      return d === 0 ? 7 : d;
    },

    /**
     * @description Nombre completo del usuario
     * @param {Object} user - Usuario
     * @returns {string}
     */
    getFullName(user) {
      return helperService.getFullName(user);
    },

    /**
     * @description Abre el modal para un slot
     * @param {Object} slot - Slot clickeado
     */
    showModal(slot) {
      if (slot.available || slot.appointment) {
        this.selectedSlot = slot;
        this.isModalVisible = true;
      }
    },

    /**
     * @description Cierra el modal
     */
    closeModal() {
      this.isModalVisible = false;
    },

    /**
     * @description Crea un sobreturno (slot instantáneo sin _id).
     *              Auto-selecciona especialidad e institución si hay una sola.
     *              UC-REG-160: attentionType incluye ambas modalidades para
     *              que el modal ofrezca Presencial y Virtual; el slot ad-hoc
     *              queda con las 2 en su array pero la cita solo usa la
     *              elegida (comportamiento estándar del producto).
     */
    startInstantAppointment() {
      const doctor = this.doctors.find((d) => d._id === this.selectedDoctorId);
      const doctorData = this.doctorDetail?.doctor || {};
      const institutions = doctorData.institutions || [];
      const specialties = this.medicalSpecialties;

      const newSlot = {
        attentionType: ["in-person", "virtual"],
        available: true,
        startsAt: null,
        endsAt: null,
        isVisibleByPatient: true,
        medicalSpecialties: this.selectedSpecialtyFilter
          ? specialties.filter((ms) => ms._id === this.selectedSpecialtyFilter)
          : specialties.length === 1
          ? specialties
          : specialties,
        doctor: doctor,
        institution: institutions.length === 1 ? institutions[0] : null,
      };
      this.selectedSlot = newSlot;
      this.isModalVisible = true;
    },

    /**
     * @description Deshabilita un slot con confirmación
     * @param {Object} slot - Slot a deshabilitar
     */
    disableSlot(slot) {
      this.$confirm("¿Desea deshabilitar el horario?", "Deshabilitar", {
        confirmButtonText: "Aceptar",
        cancelButtonText: "Cancelar",
        type: "warning",
      }).then(() => {
        slotService
          .disableSlotById({
            slotId: slot._id,
            params: { relocate: false, remove: false },
          })
          .then(() => {
            const index = this.slots.findIndex((s) => s._id === slot._id);
            if (index >= 0) {
              this.$set(this.slots, index, {
                ...this.slots[index],
                available: false,
                appointment: undefined,
              });
            }
            this.$message.success("Se deshabilitó el horario con éxito");
          });
      });
    },

    /**
     * @description Habilita un slot deshabilitado
     * @param {Object} slot - Slot a habilitar
     */
    enableSlot(slot) {
      slotService.enableSlotById({ slotId: slot._id }).then(() => {
        const index = this.slots.findIndex((s) => s._id === slot._id);
        if (index >= 0) {
          this.$set(this.slots, index, {
            ...this.slots[index],
            available: true,
          });
        }
        this.$message.success("Se habilitó el horario con éxito");
      });
    },

    /**
     * @description Callback cuando un paciente es admitido, refresca el calendario
     */
    onPatientAdmitted() {
      this.admissionDialogVisible = false;
      this.fetchSlots();
    },

    /**
     * @description Cancela una cita con confirmación
     * @param {Object} slot - Slot con la cita a cancelar
     */
    cancelAppointment(slot) {
      this.$confirm("¿Desea cancelar la cita?", "Cancelar", {
        confirmButtonText: "Aceptar",
        cancelButtonText: "Cancelar",
        type: "warning",
      }).then(() => {
        appointmentService.cancelAppointment(slot.appointment._id).then(() => {
          const index = this.slots.findIndex((s) => s._id === slot._id);
          if (index >= 0) {
            this.$set(this.slots, index, {
              ...this.slots[index],
              available: true,
              appointment: undefined,
            });
          }
          this.$message.success("Turno cancelado con éxito");
        });
      });
    },
  },
};
</script>

<template lang="pug">
section.calendar-view
  BaseSubNav(navigationType="appointment" :showNavigation="true")
    template(slot="actions")
      //- Admin/HealthAdmin: selector de doctor (requerido)
      el-select(
        v-if="!isDoctor"
        v-model="selectedDoctorId"
        placeholder="Seleccionar médico"
        filterable
        clearable
        size="small"
        :loading="isLoadingDoctors"
      )
        el-option(
          v-for="doc in doctors"
          :key="doc._id"
          :label="getFullName(doc)"
          :value="doc._id"
        )

      //- Filtro por especialidad (doctor con >1 especialidad, o admin)
      el-select(
        v-if="showSpecialtyFilter"
        v-model="selectedSpecialtyFilter"
        placeholder="Todas las especialidades"
        filterable
        clearable
        size="small"
      )
        el-option(
          v-for="ms in medicalSpecialties"
          :key="ms._id"
          :label="ms.name"
          :value="ms._id"
        )

      .button.button--blue.is-compact(
        v-if="!isPatient"
        @click="admissionDialogVisible = true"
      ) Admisión
      .button.button--blue.is-compact(
        v-if="canLoadSlots && !isPatient"
        @click="startInstantAppointment"
      ) + Sobreturno

  //- Toolbar: navigation + view toggle
  .cal-toolbar
    .cal-toolbar__left
      button.cal-toolbar__btn.cal-toolbar__btn--today(@click="goToToday") Hoy
      button.cal-toolbar__btn(@click="goPrev")
        micon(name="arrow_back")
      button.cal-toolbar__btn.cal-toolbar__btn--flip(@click="goNext")
        micon(name="arrow_back")
      span.cal-toolbar__title {{ navigationTitle }}

    .cal-toolbar__right
      .cal-toolbar__view-toggle
        button.cal-toolbar__view-btn(
          :class="{ 'is-active': viewMode === 'day' }"
          @click="viewMode = 'day'"
        ) Día
        button.cal-toolbar__view-btn(
          :class="{ 'is-active': viewMode === 'week' }"
          @click="viewMode = 'week'"
        ) Semana
        button.cal-toolbar__view-btn(
          :class="{ 'is-active': viewMode === 'month' }"
          @click="viewMode = 'month'"
        ) Mes

  //- Loading
  spinner(v-if="isLoading")

  //- Empty state: solo cuando no hay doctor seleccionado (admin)
  .cal-empty(v-else-if="!canLoadSlots && !isDoctor")
    p Seleccione un médico para ver su agenda

  //- Month view (simplified grid)
  template(v-else-if="viewMode === 'month'")
    .cal-month
      .cal-month__header
        .cal-month__header-cell(v-for="d in 7" :key="d") {{ ['LUN','MAR','MIÉ','JUE','VIE','SÁB','DOM'][d - 1] }}
      .cal-month__body
        .cal-month__cell(
          v-for="(day, idx) in visibleDays"
          :key="idx"
          :class="{ 'is-today': isToday(day), 'is-past': isPastDay(day) }"
          :style="idx === 0 ? { gridColumnStart: monthStartColumn(day) } : null"
          @click="currentDate = day; viewMode = 'day'"
        )
          .cal-month__day-number {{ formatDayNumber(day) }}
          .cal-month__slot-count(v-if="slotsForDay(day).length")
            span.cal-month__dot.cal-month__dot--free(v-if="slotsForDay(day).some(s => s.available)")
            span.cal-month__dot.cal-month__dot--busy(v-if="slotsForDay(day).some(s => !s.available && s.appointment)")
            span {{ slotsForDay(day).length }} turnos

  //- Day / Week view (hourly grid — siempre se muestra aunque no haya slots)
  template(v-else)
    .cal-grid-wrapper
      .cal-grid
        //- Header row
        .cal-grid__header
          .cal-grid__time-gutter
          .cal-grid__day-header(
            v-for="day in visibleDays"
            :key="day.toISOString()"
            :class="{ 'is-today': isToday(day), 'is-past': isPastDay(day) }"
          )
            .cal-grid__day-name {{ formatDayHeader(day) }}
            .cal-grid__day-number(:class="{ 'is-today-num': isToday(day) }") {{ formatDayNumber(day) }}

        //- Body: time gutter + day columns
        .cal-grid__body
          .cal-grid__time-gutter
            .cal-grid__time-label(
              v-for="h in gridHours"
              :key="h.hour"
              :style="{ height: PX_PER_30MIN * 2 + 'px' }"
            ) {{ h.label }}

          .cal-grid__columns
            .cal-grid__day-col(
              v-for="day in visibleDays"
              :key="day.toISOString()"
              :class="{ 'is-past': isPastDay(day) }"
            )
              //- Hour lines
              .cal-grid__hour-line(
                v-for="h in gridHours"
                :key="h.hour"
                :style="{ top: ((h.hour - GRID_START_HOUR) * PX_PER_30MIN * 2) + 'px' }"
              )
              .cal-grid__half-line(
                v-for="h in gridHours"
                :key="'half-' + h.hour"
                :style="{ top: ((h.hour - GRID_START_HOUR) * PX_PER_30MIN * 2 + PX_PER_30MIN) + 'px' }"
              )

              //- Slot blocks
              //- UC-REG-080: cada slot muestra iconos de tipo de atención
              //- (local_hospital = presencial, videocam = virtual) en la
              //- esquina inferior derecha para que el médico distinga sin
              //- abrir el modal.
              .cal-slot(
                v-for="slot in slotsForDay(day)"
                :key="slot._id"
                :class="[slotClass(slot), { 'is-past': isPastSlot(slot) }]"
                :style="{ top: slotTop(slot) + 'px', height: slotHeight(slot) + 'px' }"
                :title="attentionTypeTooltip(slot)"
                @click.stop="showModal(slot)"
              )
                .cal-slot__time {{ slotTime(slot) }}
                .cal-slot__label {{ slotLabel(slot) }}
                .cal-slot__attention-types(v-if="attentionTypeIcons(slot).length")
                  micon.cal-slot__attention-icon(
                    v-for="t in attentionTypeIcons(slot)"
                    :key="t.icon"
                    :name="t.icon"
                    width="11"
                    height="11"
                  )

                //- Context menu
                .cal-slot__actions(@click.stop)
                  el-dropdown(trigger="click" size="mini")
                    span.cal-slot__more(@click.stop)
                      micon(name="more_horiz" width="14" height="14")
                    el-dropdown-menu(slot="dropdown")
                      el-dropdown-item(v-if="slot.appointment" @click.native="cancelAppointment(slot)")
                        | Cancelar cita
                      el-dropdown-item(v-if="slot.available || slot.appointment" @click.native="disableSlot(slot)")
                        | Deshabilitar horario
                      el-dropdown-item(v-if="!slot.available && !slot.appointment" @click.native="enableSlot(slot)")
                        | Habilitar horario

  //- Appointment modal
  modal-appointment(
    v-if="isModalVisible && isAppointmentBookingEnabled"
    :show-dialog-modal="isModalVisible"
    :selected-slot="selectedSlot"
    :selectedMedicalSpecialty="getSelectedMedicalSpecialty"
    @close="closeModal"
  )

  //- Admission dialog
  admission-dialog(
    :visible.sync="admissionDialogVisible"
    @admitted="onPatientAdmitted"
  )
</template>

<style lang="scss" scoped>
/* =========================================
   Toolbar
   ========================================= */
.cal-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  margin-bottom: 8px;

  &__left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border: 1px solid $border-color;
    border-radius: 8px;
    padding: 6px 12px;
    font-size: 13px;
    font-weight: 500;
    color: $text-primary-new;
    cursor: pointer;
    transition: all 0.15s ease;

    .micon {
      width: 16px;
      height: 16px;
      fill: $text-secondary-new;
    }

    &:hover {
      background: #f1f5f9;
    }

    &--today {
      font-weight: 600;
      color: $navy;
      border-color: $navy;
    }

    &--flip {
      .micon {
        transform: scaleX(-1);
      }
    }
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: $navy;
    margin-left: 4px;
    text-transform: capitalize;
  }

  &__right {
    display: flex;
    align-items: center;
  }

  &__view-toggle {
    display: flex;
    border: 1px solid $border-color;
    border-radius: 8px;
    overflow: hidden;
  }

  &__view-btn {
    background: #fff;
    border: none;
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 500;
    color: $text-secondary-new;
    cursor: pointer;
    transition: all 0.15s ease;

    & + & {
      border-left: 1px solid $border-color;
    }

    &:hover {
      background: #f1f5f9;
    }

    &.is-active {
      background: $navy;
      color: #fff;
    }
  }
}

/* =========================================
   Empty state
   ========================================= */
.cal-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: $text-secondary-new;

  p {
    font-size: 15px;
    margin: 0;
  }
}

/* =========================================
   Grid (Day / Week view)
   ========================================= */
.cal-grid-wrapper {
  background: #fff;
  border: 1px solid $border-color;
  border-radius: 12px;
  overflow: hidden;
}

.cal-grid {
  display: flex;
  flex-direction: column;

  &__header {
    display: flex;
    border-bottom: 1px solid $border-color;
    position: sticky;
    top: 0;
    z-index: 2;
    background: #fff;
  }

  &__time-gutter {
    flex: 0 0 56px;
    min-width: 56px;
  }

  &__day-header {
    flex: 1;
    text-align: center;
    padding: 10px 4px;
    border-left: 1px solid #f1f5f9;

    &.is-today {
      background: rgba(0, 196, 161, 0.04);
    }
  }

  &__day-name {
    font-size: 11px;
    font-weight: 600;
    color: $text-secondary-new;
    letter-spacing: 0.04em;
  }

  &__day-number {
    font-size: 22px;
    font-weight: 700;
    color: $text-primary-new;
    line-height: 1.2;

    &.is-today-num {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: $navy;
      color: #fff;
    }
  }

  &__body {
    display: flex;
    overflow-y: auto;
    max-height: calc(100vh - 280px);
  }

  &__time-label {
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;
    padding: 0 8px;
    font-size: 11px;
    font-weight: 500;
    color: $text-secondary-new;
    transform: translateY(-7px);
  }

  &__columns {
    display: flex;
    flex: 1;
    position: relative;
  }

  &__day-col {
    flex: 1;
    position: relative;
    border-left: 1px solid #f1f5f9;
    height: v-bind('gridHeight + "px"');
  }

  &__hour-line {
    position: absolute;
    left: 0;
    right: 0;
    border-top: 1px solid #e8ecf0;
  }

  &__half-line {
    position: absolute;
    left: 0;
    right: 0;
    border-top: 1px dashed #f1f5f9;
  }
}

/* =========================================
   Slot blocks
   ========================================= */
.cal-slot {
  position: absolute;
  left: 2px;
  right: 2px;
  border-radius: 4px;
  padding: 2px 4px;
  margin-bottom: 1px;
  cursor: pointer;
  overflow: hidden;
  z-index: 1;
  transition: box-shadow 0.15s ease;
  font-size: 10px;
  line-height: 1.2;

  &:hover {
    z-index: 3;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  &--free {
    background: #e0f7f1;
    border-left: 3px solid #00c4a1;
    color: #065f46;
  }

  &--busy {
    background: #dbeafe;
    border-left: 3px solid #3b82f6;
    color: #1e3a5f;
  }

  &--disabled {
    background: #f1f5f9;
    border-left: 3px solid #94a3b8;
    color: #64748b;
    opacity: 0.7;
  }

  &--instant {
    background: #fff3e0;
    border-left: 3px solid #f97316;
    color: #7c2d12;
  }

  &__time {
    font-weight: 600;
    font-size: 10px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__label {
    font-size: 9px;
    opacity: 0.8;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  // UC-REG-080: contenedor de iconos de tipo de atención. Va en la esquina
  // inferior derecha para no chocar con el menú contextual (top-right) ni
  // con la hora/label (top-left).
  &__attention-types {
    position: absolute;
    bottom: 2px;
    right: 4px;
    display: flex;
    align-items: center;
    gap: 2px;
    opacity: 0.72; // sutil pero legible sobre los fondos pastel
    pointer-events: none; // el click sigue funcionando en el slot
  }

  &__attention-icon {
    display: inline-flex;
    width: 11px;
    height: 11px;
    color: currentColor;
  }

  &:hover &__attention-types {
    opacity: 1;
  }

  &__actions {
    position: absolute;
    top: 2px;
    right: 2px;
    opacity: 0;
    transition: opacity 0.15s ease;
  }

  &:hover &__actions {
    opacity: 1;
  }

  &__more {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.8);
    cursor: pointer;

    .micon {
      fill: $text-secondary-new;
    }

    &:hover {
      background: #fff;
    }
  }
}

/* =========================================
   Month view
   ========================================= */
.cal-month {
  background: #fff;
  border: 1px solid $border-color;
  border-radius: 12px;
  overflow: hidden;

  &__header {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    border-bottom: 1px solid $border-color;
  }

  &__header-cell {
    padding: 10px;
    text-align: center;
    font-size: 11px;
    font-weight: 700;
    color: $text-secondary-new;
    letter-spacing: 0.04em;
  }

  &__body {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
  }

  &__cell {
    min-height: 80px;
    padding: 8px;
    border-right: 1px solid #f1f5f9;
    border-bottom: 1px solid #f1f5f9;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: #f8fafc;
    }

    &.is-today {
      background: rgba(0, 196, 161, 0.04);
    }

    &:nth-child(7n) {
      border-right: none;
    }
  }

  &__day-number {
    font-size: 13px;
    font-weight: 600;
    color: $text-primary-new;
    margin-bottom: 4px;
  }

  &__slot-count {
    font-size: 11px;
    color: $text-secondary-new;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    display: inline-block;

    &--free {
      background: #00c4a1;
    }

    &--busy {
      background: #3b82f6;
    }
  }
}

// ─── Días pasados griseados ───────────────────────────────────────────────
// El calendario es prospectivo. Los días pasados se griseaan: columnas con un
// fondo casi imperceptible, slots tomados con un gris medio (se distinguen) y
// slots "disponibles" que sobrevivieron a la ventana de 2 días del cron de
// cleanup, en gris claro. Los slots pasados siguen siendo clickeables —
// abren el detalle de la atención.

.cal-grid__day-header.is-past {
  .cal-grid__day-name,
  .cal-grid__day-number {
    color: #94a3b8;
  }
}

.cal-grid__day-col.is-past {
  background: #f4f6f8;
}

.cal-slot.is-past {
  // Slot tomado pasado — gris medio, texto opaco
  &.cal-slot--busy,
  &.cal-slot--instant {
    background: #e1e5ea;
    color: #94a3b8;
    border-color: #cbd5e1;
  }

  // Slot libre que sobrevivió al cron — gris claro, casi invisible
  &.cal-slot--free {
    background: #eef1f4;
    color: #cbd5e1;
    border-color: #d6dbe1;
  }
}

.cal-month__cell.is-past {
  background: #f4f6f8;

  .cal-month__day-number {
    color: #94a3b8;
  }
}
</style>
